import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DateSelectField } from "@/components/ui/date-select-field";
import { Separator } from "@/components/ui/separator";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle } from "lucide-react";
import * as z from "zod";
import { trackConversion } from "@/lib/utils";

// Add helper to track form activity
const trackFormActivity = () => {
  localStorage.setItem("formActivityTimestamp", new Date().toISOString());
  localStorage.setItem("activeFormType", "consultation");
};

// Schema for Step 1
const step1Schema = z.object({
  name: z.string().min(1, "Full name is required"),
  reasonForVisit: z.string().min(1, "Reason for visit is required"),
});

// Schema for Step 3 (Contact Info) - requires email OR phone
const step3Schema = z
  .object({
    email: z
      .string()
      .transform((val) => (val?.trim() === "" ? undefined : val)) // Treat empty string as undefined
      .pipe(z.string().email("Invalid email format").optional()),
    phone: z
      .string()
      .transform((val) => (val?.trim() === "" ? undefined : val)) // Treat empty string as undefined
      .pipe(
        z
          .string()
          .regex(
            /^\+?[\d\s-()]{10,}$/,
            "Phone number must contain at least 10 digits",
          )
          .optional(),
      ),
  })
  .refine((data) => data.email || data.phone, {
    message: "Either email or phone number is required",
    path: ["email"], // Apply error message primarily to email field for display
  });

interface AppointmentFormProps {
  isMensHealth?: boolean;
}

export default function AppointmentForm({
  isMensHealth = false,
}: AppointmentFormProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    reasonForVisit: "",
    preferredDate: undefined as Date | undefined,
    preferredTime: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Track form activity on mount and when user interacts
  useEffect(() => {
    // Track when form is mounted (user is on a page with the form)
    trackFormActivity();

    // Clear activity when component unmounts
    return () => {
      // Only clear if this was the active form
      if (localStorage.getItem("activeFormType") === "consultation") {
        localStorage.removeItem("activeFormType");
      }
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Track activity when user types
    trackFormActivity();

    // Clear error when user starts typing
    if (
      errors[name] ||
      ((name === "email" || name === "phone") && errors.email)
    ) {
      // Clear shared 'email' error path
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        // If clearing email/phone, also clear the shared error path if it exists
        if ((name === "email" || name === "phone") && newErrors.email) {
          delete newErrors.email;
        }
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Track activity when user makes selections
    trackFormActivity();
  };

  const handleDateChange = (date: Date | undefined) => {
    setFormData((prev) => ({ ...prev, preferredDate: date }));

    // Track activity when user changes date
    trackFormActivity();

    // Clear potential date errors if needed (though step 2 has no validation currently)
    if (errors.preferredDate) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.preferredDate;
        return newErrors;
      });
    }
  };

  // Validator for Step 1
  const validateStep1 = () => {
    try {
      step1Schema.parse({
        name: formData.name,
        reasonForVisit: formData.reasonForVisit,
      });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: { [key: string]: string } = {};
        error.errors.forEach((err) => {
          const path = err.path[0] as string;
          newErrors[path] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  // Validator for Step 3
  const validateStep3 = () => {
    try {
      step3Schema.parse({ email: formData.email, phone: formData.phone });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: { [key: string]: string } = {};
        error.errors.forEach((err) => {
          const path = err.path[0] as string;
          // Handle refine error path specifically if needed, otherwise use field path
          newErrors[path] = err.message;
        });
        // Ensure the primary 'email' error is set for the refine validation failure
        if (
          !newErrors.email &&
          !newErrors.phone &&
          error.errors.some((e) => e.code === "custom")
        ) {
          const refineError = error.errors.find((e) => e.code === "custom");
          if (refineError) {
            newErrors["email"] = refineError.message; // Assign refine error message to email field
          }
        }
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleNextStep = () => {
    if (step === 1) {
      const isValid = validateStep1();
      if (isValid) {
        setStep(2);
      }
    } else if (step === 2) {
      // No validation needed to proceed from step 2 to 3 currently
      setStep(3);
    }
  };

  const handlePrevStep = () => {
    setStep((prev) => Math.max(1, prev - 1)); // Go back one step, min step 1
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    if (step === 3) {
      const isValid = validateStep3();
      if (isValid) {
        // Restructure payload to match the backend /forms endpoint expectation
        const payload = {
          formType: "consultation",
          data: {
            name: formData.name,
            reasonForVisit: formData.reasonForVisit,
            preferredDate: formData.preferredDate
              ? formData.preferredDate.toISOString().split("T")[0]
              : undefined, // Format date if exists
            preferredTime: formData.preferredTime,
            email: formData.email,
            phone: formData.phone,
          },
        };

        try {
          setSubmitting(true);
          // Use environment variable for API URL
          const response = await fetch(
            import.meta.env.PUBLIC_API_URL + "forms",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            },
          );

          if (!response.ok) {
            let errorData;
            try {
              errorData = await response.json();
            } catch (jsonError) {
              throw new Error(
                `HTTP error ${response.status}: Failed to submit consultation request`,
              );
            }
            throw new Error(
              errorData?.error || "Failed to submit consultation request",
            );
          }

          setSubmitted(true);

          // Track conversion with appropriate value and customer data
          const conversionValue = isMensHealth ? 5900 : 3500;
          const enhancedData: { email?: string; phone_number?: string } = {};

          if (formData.email) enhancedData.email = formData.email;
          if (formData.phone) enhancedData.phone_number = formData.phone;

          trackConversion(
            isMensHealth
              ? "consultation_request_mens_health"
              : "consultation_request_neuropathy",
            conversionValue,
            { form_type: "consultation", is_mens_health: isMensHealth },
            enhancedData,
          );
        } catch (error) {
          console.error("Error submitting consultation:", error);
          setApiError(
            error instanceof Error
              ? error.message
              : "An unknown error occurred.",
          );
        } finally {
          setSubmitting(false);
        }
      }
    } else {
      handleNextStep();
    }
  };

  if (submitted) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-center">
            Consultation Request Received
          </CardTitle>
          <CardDescription className="text-center">
            Thank you for requesting a consultation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <div className="flex justify-center">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <p className="text-muted-foreground">
            We've received your consultation request. You'll receive a quick
            confirmation call to finalize your consultation details using the
            contact info provided.
          </p>
        </CardContent>
      </Card>
    );
  }

  const stepsInfo = [
    { id: 1, label: "Basic Info" },
    { id: 2, label: "Details" },
    { id: 3, label: "Contact" },
  ];

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-center text-primary-background">
          Fill out the form below to schedule
        </CardTitle>
        <div className="w-full max-w-xs mx-auto mt-4">
          <div className="flex justify-between items-center">
            {stepsInfo.map((s, index) => (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      step >= s.id
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {s.id}
                  </div>
                  <span
                    className={`text-xs mt-1 ${step >= s.id ? "text-primary" : "text-muted-foreground"}`}
                  >
                    {s.label}
                  </span>
                </div>
                {index < stepsInfo.length - 1 && (
                  <div className="flex-1 h-1 mx-2 bg-muted relative">
                    <div
                      className={`absolute top-0 left-0 h-full bg-primary transition-all duration-300 ${
                        step > s.id ? "w-full" : "w-0"
                      }`}
                    ></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {step === 1 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="name">Name*</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? "border-red-500" : ""}
                  placeholder="Enter your name"
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="reasonForVisit">Reason for Visit*</Label>
                <Textarea
                  id="reasonForVisit"
                  name="reasonForVisit"
                  value={formData.reasonForVisit}
                  onChange={handleChange}
                  placeholder="Briefly describe your condition or reason for visit"
                  rows={3}
                  className={errors.reasonForVisit ? "border-red-500" : ""}
                />
                {errors.reasonForVisit && (
                  <p className="text-sm text-red-500">
                    {errors.reasonForVisit}
                  </p>
                )}
              </div>
              <div className="pt-4">
                <Button
                  type="button"
                  onClick={handleNextStep}
                  className="w-full"
                >
                  Next →
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="preferredDate">Preferred Date</Label>
                <DateSelectField
                  value={formData.preferredDate}
                  onChange={handleDateChange}
                  placeholder="Select preferred date"
                  disabledDates={(date) => date < new Date()}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="preferredTime">Preferred Time</Label>
                <Select
                  value={formData.preferredTime}
                  onValueChange={(value) =>
                    handleSelectChange("preferredTime", value)
                  }
                >
                  <SelectTrigger id="preferredTime">
                    <SelectValue placeholder="Select a time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">
                      Morning (9AM - 12PM)
                    </SelectItem>
                    <SelectItem value="afternoon">
                      Afternoon (12PM - 5PM)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  onClick={handlePrevStep}
                  variant="outline"
                  className="flex-1"
                >
                  ← Back
                </Button>
                <Button
                  type="button"
                  onClick={handleNextStep}
                  className="flex-1"
                >
                  Next →
                </Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? "border-red-500" : ""}
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              <div className="relative py-2">
                <Separator />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-card px-2 text-xs text-muted-foreground">
                    OR
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? "border-red-500" : ""}
                  placeholder="Enter your phone number"
                />
                {errors.phone && !errors.email && (
                  <p className="text-sm text-red-500">{errors.phone}</p>
                )}
              </div>
              <div className="pt-2 text-sm text-muted-foreground">
                <p>We'll reach out to confirm your consultation details.</p>
              </div>
              {apiError && (
                <p className="mt-2 text-center text-sm text-red-500">
                  {apiError}
                </p>
              )}
              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  onClick={handlePrevStep}
                  variant="outline"
                  className="flex-1"
                  disabled={submitting}
                >
                  ← Back
                </Button>
                <Button type="submit" className="flex-1" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
